import { memo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import type { WorkflowNodeType } from "@/types/aiToolkit";
import { NODE_META } from "./nodeMeta";
import { STATUS_COLORS, type NodeRunStatus } from "./runStatusPure";

export interface CanvasNodeData {
  nodeType: WorkflowNodeType;
  label: string;
  subtitle?: string;
  /** 4A.8: live run decoration */
  runStatus?: NodeRunStatus;
  runMs?: number | null;
  runCompletions?: number;
  [k: string]: unknown;
}

/**
 * Presentational node for the React Flow canvas. Branch nodes (condition/gates)
 * expose named source handles matching the branch keys the engine emits
 * (nodeExecutors.ts): confidence_pass/confidence_fail, quality_pass/quality_fail,
 * true/false. Edge.sourceHandle is what workflowEngine reads to pick a branch.
 */
function branchHandles(type: WorkflowNodeType): string[] {
  if (type === "confidence-gate") return ["confidence_pass", "confidence_fail"];
  if (type === "quality-gate") return ["quality_pass", "quality_fail"];
  if (type === "condition") return ["true", "false"];
  if (type === "loop") return ["loop", "done"]; // 4A.4 — body cycle vs continue
  return [];
}

export const CanvasNode = memo(({ data, selected }: NodeProps) => {
  const d = data as CanvasNodeData;
  const meta = NODE_META[d.nodeType];
  const color = meta?.color ?? "#64748b";
  const branches = branchHandles(d.nodeType);
  const isTrigger = d.nodeType === "trigger";
  const isOutput = d.nodeType === "output";
  const rs = d.runStatus;
  const rsColor = rs ? STATUS_COLORS[rs] : undefined;

  return (
    <div
      style={{
        borderLeft: `4px solid ${color}`,
        borderRadius: 8,
        minWidth: 180,
        boxShadow: rs
          ? `0 0 0 2px ${rsColor}${rs === "running" ? ", 0 0 10px 2px " + rsColor : ""}`
          : selected
          ? `0 0 0 2px ${color}`
          : "0 1px 3px rgba(0,0,0,0.18)",
        background: "var(--card, #fff)",
        color: "var(--card-foreground, #0f172a)",
        fontSize: 13,
        transition: "box-shadow 200ms",
      }}
    >
      {!isTrigger && <Handle type="target" position={Position.Left} />}
      <div style={{ padding: "8px 10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 999, background: color, flex: "0 0 auto" }} />
          <strong style={{ fontSize: 12, letterSpacing: 0.2 }}>{meta?.label ?? d.nodeType}</strong>
          {meta && !meta.executes && (
            <span title="Declared but not yet executed by the engine (Phase 4A.3/4A.4)"
              style={{ marginLeft: "auto", fontSize: 10, color: "#b45309", border: "1px solid #f59e0b", borderRadius: 4, padding: "0 4px" }}>
              stub
            </span>
          )}
        </div>
        <div style={{ marginTop: 4, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {d.label || "(unnamed)"}
        </div>
        {rs && (
          <div style={{ marginTop: 3, fontSize: 10, fontWeight: 600, color: rsColor, display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: rsColor }} />
            {rs}
            {typeof d.runMs === "number" && rs === "completed" ? ` · ${d.runMs}ms` : ""}
            {d.runCompletions && d.runCompletions > 1 ? ` · ×${d.runCompletions}` : ""}
          </div>
        )}
        {d.subtitle && (
          <div style={{ marginTop: 2, fontSize: 11, opacity: 0.65, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {d.subtitle}
          </div>
        )}
      </div>

      {d.nodeType === "crew" ? (
        <>
          {/* delegation edges to worker agent nodes */}
          <Handle id="worker" type="source" position={Position.Bottom} title="connect worker agents" style={{ background: color }} />
          {/* continuation after the crew finishes */}
          <Handle type="source" position={Position.Right} title="next (after crew finishes)" />
        </>
      ) : branches.length > 0 ? (
        branches.map((b, i) => (
          <Handle
            key={b}
            id={b}
            type="source"
            position={Position.Right}
            style={{ top: 18 + i * 20 }}
            title={b}
          />
        ))
      ) : (
        !isOutput && <Handle type="source" position={Position.Right} />
      )}
    </div>
  );
});

CanvasNode.displayName = "CanvasNode";
